import { useState, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { IFormInput } from "./types";
import { LargeContentBox } from "../../components/common";
import { FormSubscription, TermsOfUse } from "./components";
import { SubscriptionPlan } from "./components/SubscriptionPlan";
import { Stack, Step, StepLabel, Stepper } from "@mui/material";
import { PaymentStep } from "./components/PaymentStep";
import { PlanTypeSelection } from "./components/PlanTypeSelection";
import { FluxPayPlan } from "./components/FluxPayPlan";
import { FluxPayConfirmation } from "./components/FluxPayConfirmation";
import { marketplaceService } from "../../services/marketplace/MarketplaceService";
import { useOrganizationContext } from "../../contexts/OrganizationContext";
import { RegistrationSuccess } from "./components/RegistrationSuccess";

const defaultValues = {
  cnpjValue: "",
  cpfValue: "",
  personName: "",
  personSurname: "",
  corporateName: "",
  cnesValue: "",
  organization: undefined,
};

export function RegisterOrganization({
  onBackToSelection,
}: {
  onBackToSelection: () => void;
}) {
  const methods = useForm<IFormInput>({ defaultValues });
  const { handleSubmit, control, setValue, getValues } = methods;
  const type = localStorage.getItem("operation");
  /** Renovação de plano: mantém seleção de planos (assinatura ou FluxPay). */
  const isRenewPlan = type === "renewPlan";
  const { organizationId } = useOrganizationContext();

  // Labels iniciais com apenas 3 steps (fluxo renew: termina em escolha de plano)
  const initialLabels = [
    "Registrar organização",
    "Termos de uso",
    "Escolha do plano",
  ];

  // Novo cadastro CNPJ: após termos vai direto ao sucesso — terceiro passo é conclusão
  const organizationCompleteLabels = [
    "Registrar organização",
    "Termos de uso",
    "Cadastro concluído",
  ];

  // Labels para o fluxo de assinatura
  const subscriptionLabels = [
    "Registrar organização",
    "Termos de uso",
    "Escolha do plano",
    "Plano de assinatura",
    "Realizar pagamento",
  ];

  // Labels para o fluxo do FluxPay
  const fluxPayLabels = [
    "Registrar organização",
    "Termos de uso",
    "Escolha do plano",
    "FluxPay",
  ];

  const [currentStep, setCurrentStep] = useState(type === "renewPlan" ? 2 : 0);
  const [selectedPlan, setSelectedPlan] = useState<any>(null);
  const [planType, setPlanType] = useState<"subscription" | "fluxpay" | null>(
    null
  );
  const [fluxPayActivated, setFluxPayActivated] = useState(false);
  const [checkingFluxPayStatus, setCheckingFluxPayStatus] = useState(false);

  // Verificar se há um parâmetro na URL indicando ativação do FluxPay
  useEffect(() => {
    const checkFluxPayActivation = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get("code");
      const state = urlParams.get("state");

      // Se temos código e state, significa que o usuário está retornando do Mercado Pago
      if (code && state && organizationId) {
        setCheckingFluxPayStatus(true);
        setPlanType("fluxpay");

        try {
          // Verificar se a extensão está ativa
          const extensionStatus =
            await marketplaceService.checkMarketplaceExtension(organizationId);

          if (extensionStatus.hasActiveExtension) {
            setFluxPayActivated(true);
            setCurrentStep(4); // Vai para a etapa de confirmação
          } else {
            // Se não estiver ativa, mantém na etapa do FluxPay
            setCurrentStep(3);
            alert(
              "Não foi possível ativar o FluxPay. Por favor, tente novamente."
            );
          }
        } catch (error) {
          console.error("Erro ao verificar status do FluxPay:", error);
          setCurrentStep(3); // Mantém na etapa do FluxPay
          alert(
            "Ocorreu um erro ao verificar o status do FluxPay. Por favor, tente novamente."
          );
        } finally {
          setCheckingFluxPayStatus(false);

          // Limpar os parâmetros da URL
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          );
        }
      }
    };

    checkFluxPayActivation();
  }, [organizationId]);

  const handleNext = () => {
    setCurrentStep((prevStep) => prevStep + 1);
  };

  // Após aceitar termos: só renew segue para escolha de plano; demais operações vão ao sucesso
  const handleAfterTermsAccepted = () => {
    if (isRenewPlan) {
      handleNext();
    } else {
      setCurrentStep(2);
    }
  };

  const handleBack = () => {
    setCurrentStep((prevStep) => prevStep - 1);
  };

  const handlePlanTypeSelection = (type: "subscription" | "fluxpay") => {
    setPlanType(type);
    handleNext();
  };

  // Rótulos do stepper: cadastro novo (sem renew) não inclui escolha de plano no fluxo principal
  const currentLabels = !isRenewPlan
    ? organizationCompleteLabels
    : planType === null
      ? initialLabels
      : planType === "fluxpay"
        ? fluxPayLabels
        : subscriptionLabels;

  // Se estamos verificando o status do FluxPay, mostra uma mensagem de carregamento
  if (checkingFluxPayStatus) {
    return (
      <LargeContentBox>
        <div style={{ textAlign: "center", padding: "50px" }}>
          <h2>Verificando status da integração com o FluxPay...</h2>
          <p>Por favor, aguarde enquanto processamos sua solicitação.</p>
        </div>
      </LargeContentBox>
    );
  }

  return (
    <FormProvider {...methods}>
      <LargeContentBox variant="content-fit">
        <Stack
          width="100%"
          spacing={2}
          sx={{
            minWidth: "600px",
            minHeight: "100%",
            maxWidth: "100%",
            width: "fit-content",
          }}
        >
          <Stepper activeStep={currentStep}>
            {currentLabels.map((key, value) => (
              <Step key={value}>
                <StepLabel>{key}</StepLabel>
              </Step>
            ))}
          </Stepper>
          {currentStep === 0 && (
            <FormSubscription
              onBackToSelection={onBackToSelection}
              getValue={getValues}
              control={control}
              handleSubmit={handleSubmit}
              navigateToNextPage={handleNext}
              setValue={setValue}
            />
          )}
          {currentStep === 1 && (
            <TermsOfUse
              handleSubmit={handleSubmit}
              onBackButtonClick={handleBack}
              onNextButtonClick={handleAfterTermsAccepted}
            />
          )}
          {currentStep === 2 && isRenewPlan && (
            <PlanTypeSelection onSelectPlanType={handlePlanTypeSelection} />
          )}
          {currentStep === 2 && !isRenewPlan && (
            <RegistrationSuccess
              displayName={getValues("corporateName") || "sua organização"}
              variant="organization"
            />
          )}
          {currentStep === 3 && planType === "subscription" && (
            <SubscriptionPlan
              setSelectedPlan={setSelectedPlan}
              handleNext={handleNext}
              handleBack={handleBack}
            />
          )}
          {currentStep === 3 && planType === "fluxpay" && (
            <FluxPayPlan handleNext={handleNext} handleBack={handleBack} />
          )}
          {currentStep === 4 && planType === "subscription" && selectedPlan && (
            <PaymentStep selectedPlan={selectedPlan} />
          )}
          {currentStep === 4 && planType === "fluxpay" && (
            <FluxPayConfirmation />
          )}
        </Stack>
      </LargeContentBox>
    </FormProvider>
  );
}
